/**
 * Single status banner for demand detail — one message, one next action.
 */

import { isPaidBeforeAssignment, isPaidStatus } from './paidBeforeAssignment.js';
import { isMatterRecordLocked, statusDisplayLabel } from './matterPipeline.js';

/**
 * @returns {{ tone: 'info'|'success'|'warn', message: string, action: string } | null}
 */
export function buildMatterStatusBanner(matter = {}) {
  if (!matter) return null;

  if (matter.Archived) {
    if (isPaidBeforeAssignment(matter.Status)) {
      return {
        tone: 'info',
        message:
          'This demand is archived. It was paid before assignment with no per-demand fee.',
        action: 'Restore it anytime from Archived demands.',
      };
    }
    if (isPaidStatus(matter.Status)) {
      return {
        tone: 'info',
        message: 'This demand is archived after payment.',
        action: 'Restore it anytime from Archived demands.',
      };
    }
    return {
      tone: 'info',
      message: 'This demand is archived.',
      action: 'Restore it anytime from Archived demands.',
    };
  }

  if (isPaidBeforeAssignment(matter.Status)) {
    return {
      tone: 'success',
      message:
        'Resolved — paid before assignment. No per-demand fee was charged.',
      action: 'Archive this demand when you are done reviewing it.',
    };
  }

  if (isPaidStatus(matter.Status)) {
    return {
      tone: 'success',
      message: `This demand is ${statusDisplayLabel(matter.Status)}.${
        matter.Paid_At ? ` Paid ${formatBannerWhen(matter.Paid_At)}.` : ''
      }${matter.Resolution_Note ? ` ${matter.Resolution_Note}` : ''}`,
      action: 'Archive this demand when you are done reviewing it.',
    };
  }

  if (isMatterRecordLocked(matter.Status)) {
    return {
      tone: 'info',
      message: 'Fields locked after attorney assignment.',
      action: 'Use Payments, Messages, History, or Notes.',
    };
  }

  return null;
}

function formatBannerWhen(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
}

/**
 * Ephemeral payment flash after Record Payment.
 * Always confirms the payment action; adds notify/accounting extras when present.
 * Demand status outcomes still live on the status banner.
 */
export function buildPaymentFlashMessage(
  resolution = {},
  { notified = false, accounting = null } = {}
) {
  const parts = [];
  const paid = Number(resolution.amountPaid);
  if (Number.isFinite(paid) && paid > 0) {
    parts.push(
      `Payment of $${paid.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} recorded.`
    );
  } else {
    parts.push('Payment recorded.');
  }
  if (notified) parts.push('Attorney notified in Messages.');
  const accountingFlash = accountingPaymentFlashMessage(accounting);
  if (accountingFlash) parts.push(accountingFlash);
  return parts.join(' ');
}

const ACCOUNTING_PROVIDER_LABELS = {
  QUICKBOOKS: 'QuickBooks',
  ZOHO_BOOKS: 'Zoho Books',
  XERO: 'Xero',
  SAGE: 'Sage',
  NETSUITE: 'NetSuite',
  FRESHBOOKS: 'FreshBooks',
  WAVE: 'Wave',
};

export function accountingPaymentFlashMessage(accounting) {
  if (!accounting?.synced) return '';
  const key = String(accounting.provider || '').toUpperCase();
  const label =
    ACCOUNTING_PROVIDER_LABELS[key] || key.replace(/_/g, ' ') || 'accounting software';
  if (accounting.mock) return `Payment posted to ${label} (demo).`;
  return `Payment posted to ${label}.`;
}

export function accountingPaymentDeleteFlashMessage(accounting) {
  if (!accounting?.deleted) return '';
  const key = String(accounting.provider || '').toUpperCase();
  const label =
    ACCOUNTING_PROVIDER_LABELS[key] || key.replace(/_/g, ' ') || 'accounting software';
  if (accounting.mock) return `Payment removed from ${label} (demo).`;
  return `Payment removed from ${label}.`;
}

export function buildPaymentDeleteFlashMessage(resolution, { accounting } = {}) {
  const parts = [];
  if (resolution?.message) parts.push(resolution.message);
  const accountingFlash = accountingPaymentDeleteFlashMessage(accounting);
  if (accountingFlash) parts.push(accountingFlash);
  return parts.join(' ');
}
